<template>
  <li
    class="checklist-step"
    :class="{
      'checklist-step--completed': step.isCompleted,
      'checklist-step--next': isNext,
    }"
  >
    <button class="checklist-step__inner" @click="onCtaClick">
      <span class="checklist-step__icon"></span>
      <p class="checklist-step__title">{{ step.title }}</p>

      <div class="checklist-step__chevron">
        <span class="iconfont iarrow-down"></span>
      </div>
    </button>
  </li>
</template>

<script setup lang="ts">
import { Mixpanel } from '@/analytics/mixpanel'

import type { ChecklistStepView } from '@/models/checklist'
import type { ChecklistDocs } from '@/models/checklist.types'

// We the only really required prop is `title`. We mark the
// rest as optional so that we can set the free initial step
// (eg. "Join Shortform").
interface PartialStep extends Partial<ChecklistStepView> {
  title: string
}

const props = defineProps<{
  step: PartialStep
  docs: ChecklistDocs
  isNext?: boolean
}>()

function onCtaClick(): void {
  if (!props.step.onClick || props.step.isCompleted) {
    return
  }

  const step = props.step as ChecklistStepView
  step.onClick(step, props.docs)
  Mixpanel.trackChecklistStepClicked(step.id!)
}
</script>

<style lang="scss" scoped>
.checklist-step {
  display: flex;
  flex-direction: column;
  justify-content: center;
  border-top: 1px solid $c-border;
  --stepPaddingInline: 24px;
  --stepPaddingBlock: 16px;

  @media (max-width: $w-mobile-s) {
    --stepPaddingInline: 12px;
    --stepPaddingBlock: 12px;
  }

  &__inner {
    display: flex;
    align-items: center;
    gap: 16px;
    flex: 1;
    padding: var(--stepPaddingBlock) var(--stepPaddingInline);
    text-align: left;
    transition: background 0.12s;

    @media (max-width: $w-mobile-s) {
      gap: 8px;
    }

    @include hover {
      background: $c-bg;
    }
  }

  &__icon {
    display: grid;
    grid-template-areas: 'stack';
    place-items: center;
    width: 22px;
    height: 22px;
    flex: none;
    border: 2px solid $c-black-13;
    border-radius: 50%;

    &:after {
      content: '';
      grid-area: stack;
    }

    @media (max-width: $w-mobile-s) {
      width: 18px;
      height: 18px;
    }
  }

  &__title {
    flex: 1;
    font-size: 16px;
    line-height: 1;
    font-weight: 500;
    color: $c-black-2;
    margin: 0;
    margin-top: 2px;

    @media (max-width: $w-mobile-s) {
      font-size: 14px;
    }
  }

  &__chevron {
    display: grid;
    place-items: center;
    --chevronSize: 24px;
    width: var(--chevronSize);
    height: var(--chevronSize);
    font-size: var(--chevronSize);
    color: $c-black-6;
    transform: rotate(-90deg);
    transition: 0.2s;

    @media (max-width: $w-mobile-s) {
      --chevronSize: 16px;
    }

    .iconfont {
      display: block;
      width: var(--chevronSize);
      height: var(--chevronSize);
    }
  }
}

.checklist-step--next {
  .checklist-step {
    &__icon {
      border-color: $c-yellow;

      &:after {
        width: 8px;
        height: 8px;
        border-radius: 8px;
        background: $c-yellow;

        @media (max-width: $w-mobile-s) {
          width: 6px;
          height: 6px;
        }
      }
    }
  }
}

.checklist-step--completed {
  .checklist-step {
    &__inner {
      background: transparent;
      cursor: auto;
    }

    &__chevron {
      display: none;
    }

    &__icon {
      background: $c-green-2;
      border: none;

      &:after {
        margin-top: -2px;
        height: 7px;
        width: 11px;
        border-bottom: 3px solid $c-white;
        border-left: 3px solid $c-white;
        transform: rotate(-45deg);

        @media (max-width: $w-mobile-s) {
          height: 5px;
          width: 8px;
          border-width: 2px;
        }
      }
    }
  }
}

.night-mode .checklist-step {
  background: $c-dark-2;
  border-color: $c-dark-3;

  &__inner {
    @include hover {
      background: $c-black-8;
    }
  }

  &__title {
    color: $c-dark-4;
  }
}

.night-mode .checklist-step--completed {
  .checklist-step {
    &__inner {
      @include hover {
        background: transparent;
      }
    }
  }
}
</style>
